import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

import {
  createSelectRepeater,
  type Params as SelectParams,
  SelectContextProvider,
} from 'Components/smallComponents/select';
import type {
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { vehicleSelected } from 'Core/actions/fitmentSearch/index';
import { tryResponseRedirect } from 'Core/epics/redirect';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  fitmentSearchFieldsSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import { logRuntimeError } from 'Diagnostics/diagnostics';
import { FacetCollection, Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import {
  hasRequiredVehicleSelection,
  isVehicleChangedOnResponse,
  updateVehicleSelection,
  type VehicleValue,
} from 'Models/vehicle';
import { getFitmentResponseFacets, getRequiredFitmentFields } from 'Modules/fitmentSearchFacets';
import { abortControllerCreator, search } from 'Modules/serverApi/index';
import { cloneSafe } from 'Utils/components';
import t from 'Utils/translate';

interface Props {
  template: TemplateFunction<VehicleDropdownsParams>;
  doNotRedirectOnVehicleSelect?: boolean;
  isAutoVehicleSelectionDisabled: boolean;
  redirectUrl?: string;
  useNativeDropdown?: boolean;
  onSelectVehicle: () => void;
}

type SelectionState = {
  selection: VehicleValue[];
  facets: FacetCollection;
  isLoading: boolean;
};

type Request = {
  signal: AbortSignal;
  selection: VehicleValue[];
  currentVehicle: Vehicle;
};

export interface VehicleDropdownsParams {
  selects: RepeaterFunctionInvoker<SelectParams>;
  allowToSetVehicle: boolean;
  allowToDiscardVehicle: boolean;
  selectVehicle: () => void;
  discardVehicle: () => void;
  template: 'active';
}

export function GarageVehicleWidget({
  template,
  doNotRedirectOnVehicleSelect,
  isAutoVehicleSelectionDisabled,
  redirectUrl,
  useNativeDropdown,
  onSelectVehicle,
}: Props): TemplateResult {
  const dispatch = useDispatch();

  const requestController = useMemo(() => abortControllerCreator(), []);

  const [selectionState, setSelectionState] = useState<SelectionState>(() => ({
    selection: [],
    facets: new FacetCollection(),
    isLoading: true,
  }));

  const baseFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFields = useSelector(fitmentSearchExtraFieldsSelector);
  const fields = useSelector(fitmentSearchFieldsSelector);
  const selectedVehicle = useSelector(selectedVehicleSelector);

  const [request, setRequest] = useState<Request>(() => ({
    signal: requestController.create(),
    selection: [],
    currentVehicle: selectedVehicle,
  }));

  const updateRequest = useCallback(
    (selection: VehicleValue[], clearFacets: boolean, currentVehicle: Vehicle) => {
      const signal = requestController.create();

      setSelectionState((previous) => ({
        selection,
        facets: clearFacets ? new FacetCollection() : previous.facets,
        isLoading: true,
      }));
      setRequest({ signal, selection, currentVehicle });
    },
    [requestController],
  );

  const commitVehicle = useCallback(
    (vehicle: Vehicle, isAutoVehicleSelection: boolean) => {
      dispatch(
        vehicleSelected(vehicle, {
          doNotRedirectOnVehicleSelect,
          redirectUrl,
          isVehicleAutoSelected: isAutoVehicleSelection,
          isSelectedFromGarageDropdowns: true,
          sendIsolatedRequest: true,
        }),
      );

      fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
      onSelectVehicle();
    },
    [dispatch, doNotRedirectOnVehicleSelect, redirectUrl, onSelectVehicle],
  );

  useEffect(() => {
    const { signal, selection, currentVehicle } = request;
    if (signal.aborted) {
      if (requestController.isCurrent(signal)) {
        updateRequest(selection, false, currentVehicle);
      }
      return;
    }

    search({ selection, pageSize: 0, mode: 'YMM', extra: { ignorePageContext: true } }, signal)
      .then((response) => {
        if (signal.aborted) {
          return;
        }

        const responseFacets = new FacetCollection(response.Facets);
        setSelectionState({
          selection: responseFacets.selection.filter((value) => fields.includes(value.field)),
          facets: responseFacets,
          isLoading: false,
        });

        tryResponseRedirect({ dispatch, action: { meta: { doNotRedirectOnVehicleSelect } } }, response);

        const facets = getResponseFacets(responseFacets, baseFields, extraFields);
        const responseFields = facets.map((facet) => facet.field);
        const requiredFields = getRequiredFitmentFields(responseFields, baseFields);
        const isAllRequiredFieldsSelected = hasRequiredVehicleSelection(facets.selection, requiredFields);
        const isAllFieldsSelected = facets.every((facet) => !!facet.selection.length);
        const responseVehicle = new Vehicle(facets.selection, fields);

        if (
          !isAutoVehicleSelectionDisabled &&
          !fitmentSearchConfig.isAutoVehicleSelectionDisabled &&
          facets.length > 0 &&
          isAllFieldsSelected &&
          isVehicleChangedOnResponse(currentVehicle, responseVehicle, isAllRequiredFieldsSelected)
        ) {
          commitVehicle(responseVehicle, true);
        }
      })
      .catch((error) => {
        if (signal.aborted) {
          return;
        }
        setSelectionState((previous) => ({
          ...previous,
          isLoading: false,
          facets: new FacetCollection(),
        }));
        logRuntimeError({ source: 'garage_vehicle_request', error });
      });

    return () => requestController.abort(signal);
  }, [
    request,
    requestController,
    baseFields,
    extraFields,
    fields,
    dispatch,
    doNotRedirectOnVehicleSelect,
    isAutoVehicleSelectionDisabled,
    commitVehicle,
    updateRequest,
  ]);

  const facets = getResponseFacets(selectionState.facets, baseFields, extraFields);
  const responseFields = facets.map((facet) => facet.field);
  const requiredFields = getRequiredFitmentFields(responseFields, baseFields);
  const isAllRequiredFieldsSelected = hasRequiredVehicleSelection(facets.selection, requiredFields);
  const allowToSetVehicle = facets.length > 0 && isAllRequiredFieldsSelected && !selectionState.isLoading;

  const onChange = (value: VehicleValue) => {
    const selection = selectionState.facets.selection.filter((value) => fields.includes(value.field));
    updateRequest(updateVehicleSelection(selection, value, fields), false, selectedVehicle);
  };

  const clear = () => {
    updateRequest([], true, selectedVehicle);
    if (facets.length > 0 && isAllRequiredFieldsSelected) {
      fitmentSearchConfig.onVehicleDiscarded();
    }
  };

  const selectVehicle = () => {
    if (!allowToSetVehicle) {
      return;
    }
    commitVehicle(Vehicle.fromSelection(facets.selection, responseFields, requiredFields, true), false);
  };

  const selects = createSelectRepeater(
    facets.length
      ? facets.map(({ field, name, facetedValues }, index) => ({
          entries: facetedValues.map((value) => ({
            value: value.value,
            term: value.term,
            payload: value.payload,
            selected: value.isSelected,
          })),
          extraClassName: 'cm_vehicle-widget_select',
          disabled: !facetedValues.length,
          hideNullOption: true,
          field,
          title: name,
          key: field,
          index,
          useNativeDropdown,
          onChange: (term) => {
            const value = facetedValues.find((value) => value.term === term);
            if (value) {
              onChange(value);
            }
          },
        }))
      : baseFields.map((field, index) => {
          const title = t(`facet_title_${field.replaceAll(' ', '_')}`) || field;
          const selected = selectionState.selection.find((value) => value.field === field);
          return {
            entries: [{ value: selected?.value || title, term: '', selected: !!selected }],
            extraClassName: 'cm_vehicle-widget_select',
            disabled: index !== 0,
            loading: index === 0 && selectionState.isLoading,
            hideNullOption: true,
            field,
            title,
            key: field,
            index,
            useNativeDropdown,
            onChange: () => {},
          };
        }),
  );

  const component = template.call({
    selects,
    allowToSetVehicle,
    allowToDiscardVehicle: selectionState.selection.length > 0,
    selectVehicle,
    discardVehicle: clear,
    template: 'active',
  });

  return (
    <SelectContextProvider>
      {cloneSafe(component, null, { appendedClasses: ['cm_column', 'cm_vehicle-widget__column'] })}
    </SelectContextProvider>
  );
}

function getResponseFacets(facets: FacetCollection, baseFields: string[], extraFields: string[]) {
  return getFitmentResponseFacets(facets, baseFields, extraFields, {
    hideExtraFields:
      !fitmentSearchConfig.isExtraFieldsAlwaysVisible &&
      (fitmentSearchConfig.isOnCategorySelectionPage || !requestConfig.hasSearchResults),
    isPartialMode: fitmentSearchConfig.isVehicleWidgetInPartialMode,
  });
}

export interface VehicleDropdownsTemplateParams {
  closeGarage: () => void;
  vehicleWidget: TemplateFunctionInvoker<VehicleDropdownsParams>;
  template: 'vehicleDropdowns';
}

export default function VehicleDropdowns({
  template,
  closeGarage,
  vehicleWidget,
}: {
  template: TemplateFunction<VehicleDropdownsTemplateParams>;
  closeGarage: () => void;
  vehicleWidget: TemplateFunctionInvoker<VehicleDropdownsParams>;
}): TemplateResult {
  return template.call({ closeGarage, vehicleWidget, template: 'vehicleDropdowns' });
}
